import { useCallback, useMemo } from 'react'
import { useQuery } from 'react-query'
import minigridAPI from '../../minigrid/service/minigridAPI'

// Source d'options du dropdown : l'endpoint dataviz `/minigrids/locations`
// (CouchDB côté api-data-platform, qui agrège côté domainModel et enrichit
// avec le Minigrid inscrit + son momi_uuid). Le `momi_uuid` est la valeur
// que les dashboards Influx utilisent comme tag d'identification ; c'est donc
// la clé attendue par les filtres dashboard. On ne garde que les entrées dont
// le minigrid est inscrit ET rattaché à un MoMi (sans uuid, pas de filtre
// possible côté Influx).
export function useMinigridNamesByUuid() {
  const locationsQuery = useQuery({
    queryKey: ['minigridLocations'],
    queryFn: () => minigridAPI.getMinigridLocations(),
    staleTime: 5 * 60 * 1000,
  })

  const { nameByUuid, options } = useMemo(() => {
    // Plusieurs `MinigridLocationEnriched` peuvent pointer sur le même
    // Minigrid inscrit (même `momi_uuid`) — on dédoublonne par uuid pour
    // éviter de répéter le même nom dans le dropdown et les légendes.
    const m = new Map<string, string>()
    for (const loc of locationsQuery.data ?? []) {
      const linked = loc.linkedMinigrid
      const uuid = linked?.momi_uuid?.trim()
      const name = linked?.minigrid_name?.trim() || loc.minigridName?.trim()
      if (!uuid || !name) continue
      if (!m.has(uuid)) m.set(uuid, name)
    }
    const opts = Array.from(m, ([uuid, name]) => ({ uuid, name })).sort(
      (a, b) => a.name.localeCompare(b.name, 'fr')
    )
    return { nameByUuid: m, options: opts }
  }, [locationsQuery.data])

  const resolveName = useCallback(
    (uuid: string): string => nameByUuid.get(uuid) ?? uuid,
    [nameByUuid]
  )

  return {
    nameByUuid,
    resolveName,
    options,
    isLoading: locationsQuery.isLoading,
  }
}

export default useMinigridNamesByUuid
